'use client';

import { CriteriaInfoMap, CriteriaMap, SECOND_MULTIPLIER } from '@/constants/broker_metrics';
import { useActiveTabBrokerMetricsMap, useActiveTabBrokerStatsMap } from '@/hooks/useBrokerMetricsSelectors';
import { fetchMissingBrokersForTab, toggleBroker } from '@/store/brokerMetricsHandlers';
import { setSelectedEndpoint } from '@/store/brokerMetricsSlice';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { Info, Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import BrokerLogo from './UI/BrokerLogo';
import CaveatBanner from './UI/CaveatBanner';
import DateRangePicker from './UI/DateRangePicker';
import MultiSelect from './UI/MultiSelect';
import PlotlyBox from './UI/PlotlyBox';

type ViewMode = 'combined' | 'individual';

const SelectedBrokerChips = () => {
    const dispatch = useAppDispatch();
    const brokers = useAppSelector((state) => state.brokerMetrics.brokers);
    const comparedBrokers = useAppSelector((state) => state.brokerMetrics.comparedBrokers);
    const loadingBrokers = useAppSelector((state) => state.brokerMetrics.loadingBrokers);
    const brokerMap = useMemo(() => new Map(brokers.map((broker) => [broker.code, broker])), [brokers]);

    if (comparedBrokers.length === 0) {
        return null;
    }

    const handleRemove = (code: string) => {
        dispatch(toggleBroker(code));
    };

    return (
        <div className='scrollbar-tiny w-full min-w-0 overflow-x-auto md:flex-1'>
            <ul className='flex w-max items-center gap-2'>
                {comparedBrokers.map((code) => {
                    const broker = brokerMap.get(code);
                    if (!broker) {
                        return null;
                    }
                    const loading = loadingBrokers[broker.code];
                    return (
                        <li
                            key={broker.code}
                            className='inline-flex h-8 shrink-0 items-center gap-[3px] rounded-[2px] border border-[#E3E3E3] bg-[#FAFAFA] px-2 py-1.5 text-xs text-[#555] md:h-9 md:gap-2 md:rounded-lg md:border-slate-300 md:bg-white md:px-2.5 md:text-slate-700'>
                            <BrokerLogo broker={broker.display_name} logoUrl={broker.logo_url} size={16} />
                            <span className='whitespace-nowrap'>{broker.display_name}</span>
                            <button
                                type='button'
                                onClick={() => handleRemove(broker.code)}
                                disabled={loading}
                                className='text-slate-400 transition-colors hover:text-slate-700 disabled:opacity-40'>
                                {loading ? (
                                    <Loader2 size={12} className='animate-spin' />
                                ) : (
                                    <>
                                        <X size={16} strokeWidth={2} className='md:hidden' />
                                        <span className='hidden md:inline'>×</span>
                                    </>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

function StatItem({ label, valueSec }: Readonly<{ label: string; valueSec: number | null }>) {
    return (
        <span>
            {label} <span className='font-semibold text-slate-700'>{valueSec === null ? '—' : `${Math.round(valueSec * SECOND_MULTIPLIER)} ms`}</span>
        </span>
    );
}

function BrokerChartSection() {
    const dispatch = useAppDispatch();
    const endPoints = useAppSelector((state) => state.brokerMetrics.endpoints);
    const selectedEndpoint = useAppSelector((state) => state.brokerMetrics.selectedEndpoint);
    const comparedBrokers = useAppSelector((state) => state.brokerMetrics.comparedBrokers);
    const metricsCache = useAppSelector((state) => state.brokerMetrics.tabs[state.brokerMetrics.selectedEndpoint]?.metricsCache);
    const loading = useAppSelector((state) => state.brokerMetrics.tabs[state.brokerMetrics.selectedEndpoint]?.loading ?? false);
    const dateRange = useAppSelector((state) => state.brokerMetrics.dateRange);
    const data = useActiveTabBrokerMetricsMap();
    const statsMap = useActiveTabBrokerStatsMap();

    const [viewMode, setViewMode] = useState<ViewMode>('combined');

    useEffect(() => {
        if (!selectedEndpoint || comparedBrokers.length === 0 || loading || !dateRange.start || !dateRange.end) {
            return;
        }
        const cache = metricsCache ?? {};
        const isMissingData = comparedBrokers.some((broker) => !cache[broker]);
        if (isMissingData) {
            dispatch(fetchMissingBrokersForTab(selectedEndpoint));
        }
    }, [dispatch, selectedEndpoint, comparedBrokers, metricsCache, loading, dateRange.start, dateRange.end]);

    const brokerEntries = Object.entries(data);
    // Individual view only charts brokers that actually have buckets in the
    // selected range - the rest are dropped rather than shown as blanks.
    const brokerEntriesWithData = brokerEntries.filter(([, brokerData]) => Object.keys(brokerData.metrics).length > 0);

    return (
        <div className='flex flex-col gap-3'>
            <div className='overflow-hidden rounded-xl border border-[#E3E3E3] bg-white md:border-slate-200 md:shadow-sm'>
                <div className='relative z-30 flex flex-col gap-3 px-4 py-3 md:gap-2 md:px-0 md:py-0'>
                    <div className='flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3 md:p-3 md:pb-2'>
                        <div className='flex w-full min-w-0 flex-col gap-1.5 md:mr-4 md:flex-1 md:flex-row md:items-center md:gap-2'>
                            <div className='w-full shrink-0 md:w-auto'>
                                <MultiSelect placeHolder='Select brokers' variant='pill' />
                            </div>
                            <SelectedBrokerChips />
                        </div>
                        <div className='flex w-full flex-col gap-2 md:w-auto md:shrink-0 md:flex-row md:flex-wrap md:items-center md:gap-x-4 md:gap-y-2'>
                            <DateRangePicker />
                            {comparedBrokers.length > 1 && (
                                <button
                                    type='button'
                                    onClick={() => setViewMode((mode) => (mode === 'combined' ? 'individual' : 'combined'))}
                                    className='hidden text-xs font-medium text-[#0085FF] transition-colors hover:text-[#0069cc] md:inline'>
                                    {viewMode === 'combined' ? 'View Broker Individually' : 'View Combined'}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className='h-px w-full bg-[#E3E3E3] md:hidden' />
                    <CaveatBanner />
                </div>

                <div className='relative z-20 flex border-y border-[#E3E3E3] md:border-slate-200'>
                    {endPoints.map((endPoint) => {
                        const active = endPoint === selectedEndpoint;
                        return (
                            <button
                                key={endPoint}
                                type='button'
                                onClick={() => dispatch(setSelectedEndpoint(endPoint))}
                                className={`flex h-8 flex-1 items-center justify-center gap-1 overflow-visible whitespace-nowrap border-b-2 px-1 py-0.5 text-xs transition-colors sm:text-sm md:h-auto md:gap-1.5 md:px-4 md:py-3 ${
                                    active
                                        ? 'border-[#0085FF] font-semibold text-[#2A2A2A] md:font-medium md:text-slate-900'
                                        : 'border-transparent bg-[#FAFAFA] font-normal text-[#2A2A2A] hover:text-slate-500 md:bg-slate-50 md:font-medium md:text-slate-700'
                                }`}>
                                {CriteriaMap[endPoint] ?? endPoint}
                                {CriteriaInfoMap[endPoint] && (
                                    <span
                                        className='tooltip tooltip-bottom z-30 inline-flex text-center text-xs font-normal before:z-[60] before:max-w-[16rem] before:whitespace-normal before:text-center after:z-[60]'
                                        data-tip={CriteriaInfoMap[endPoint]}>
                                        <Info size={14} className='text-slate-400' />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className={`relative p-3 transition-opacity sm:p-4 ${loading ? 'opacity-50' : ''}`}>
                    {loading && (
                        <div className='absolute right-4 top-4 z-10 flex items-center gap-1.5 text-xs text-slate-400'>
                            <Loader2 size={14} className='animate-spin' />
                            Updating
                        </div>
                    )}

                    {viewMode === 'combined' || brokerEntries.length <= 1 ? (
                        <div className='h-[26rem] w-full'>
                            <PlotlyBox data={data} />
                        </div>
                    ) : (
                        <div className='flex flex-col gap-8'>
                            {brokerEntriesWithData.length === 0 && (
                                <div className='flex h-[16rem] w-full items-center justify-center rounded-lg border border-dashed border-slate-200 text-xs text-slate-400'>
                                    No data for the selected brokers in this date range
                                </div>
                            )}
                            {brokerEntriesWithData.map(([code, brokerData]) => {
                                const stats = statsMap[code]?.stats;
                                return (
                                    <div key={code} className='flex flex-col gap-2'>
                                        <div className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'>
                                            <h4 className='text-sm font-semibold text-slate-800'>{brokerData.displayName}</h4>
                                            {stats && (
                                                <div className='flex items-baseline gap-3 text-xs text-slate-500'>
                                                    <StatItem label='Median' valueSec={stats.median} />
                                                    <StatItem label='P90' valueSec={stats.percentile_90} />
                                                    <StatItem label='P99' valueSec={stats.percentile_99} />
                                                </div>
                                            )}
                                        </div>
                                        <div className='h-[16rem] w-full'>
                                            <PlotlyBox data={{ [code]: brokerData }} showLegend={false} compact />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className='w-full text-[10px] leading-4 text-[#555] md:text-sm md:leading-normal md:text-slate-600'>
                The graph above shows the API response times for different brokers. This visual comparison allows you to quickly identify which
                brokers provide the fastest and most reliable service, crucial for executing timely trades. AlgoTest is dedicated to supporting the
                Indian trading community with transparent and actionable insights.
            </div>
        </div>
    );
}

export default BrokerChartSection;
